export interface FollowPostNotice {
  id: string;
  userId: string;
  actorId: string;
  type: "follow_post";
  postId: string;
  message: string;
}

export function followPostNotice(value: unknown, currentUserId: string): FollowPostNotice | null {
  if (!value || typeof value !== "object") return null;
  const notice = value as Partial<FollowPostNotice>;
  if (notice.type !== "follow_post" || notice.userId !== currentUserId || notice.actorId === currentUserId) return null;
  if (![notice.id, notice.actorId, notice.postId, notice.message].every((part) => typeof part === "string" && part.length > 0)) return null;
  return notice as FollowPostNotice;
}

export function noticeLink(id: string): string {
  return `/messages?notification=${encodeURIComponent(id)}`;
}

export function browserNotificationsSupported(): boolean {
  return typeof window !== "undefined" && window.isSecureContext && "Notification" in window && "serviceWorker" in navigator;
}

const preferenceKey = (userId: string) => `share-moments:browser-notifications:${userId}`;

export function browserNotificationsEnabled(userId: string): boolean {
  if (!browserNotificationsSupported() || Notification.permission !== "granted") return false;
  try { return localStorage.getItem(preferenceKey(userId)) === "enabled"; }
  catch { return false; }
}

export function setBrowserNotificationsEnabled(userId: string, enabled: boolean): void {
  localStorage.setItem(preferenceKey(userId), enabled ? "enabled" : "disabled");
}

export async function notificationWorker(): Promise<ServiceWorkerRegistration> {
  const registration = await navigator.serviceWorker.register("/notifications-sw.js", { scope: "/notification-events/" });
  if (registration.active?.state === "activated") return registration;
  const worker = registration.installing ?? registration.waiting ?? registration.active;
  if (!worker) throw new Error("通知暂时无法开启，请稍后重试。");
  await new Promise<void>((resolve, reject) => {
    const finish = () => { worker.removeEventListener("statechange", changed); window.clearTimeout(timeout); };
    const changed = () => {
      if (worker.state === "activated") { finish(); resolve(); }
      else if (worker.state === "redundant") { finish(); reject(new Error("通知暂时无法开启，请稍后重试。")); }
    };
    const timeout = window.setTimeout(() => { finish(); reject(new Error("通知暂时无法开启，请稍后重试。")); }, 10_000);
    worker.addEventListener("statechange", changed);
    changed();
  });
  return registration;
}

export async function showBrowserPostNotice(notice: FollowPostNotice, stillActive: () => boolean): Promise<void> {
  if (!stillActive() || !browserNotificationsEnabled(notice.userId) || document.visibilityState !== "hidden") return;
  const registration = await notificationWorker();
  // Account or visibility may change while the worker starts.
  if (!stillActive() || !browserNotificationsEnabled(notice.userId) || document.visibilityState !== "hidden") return;
  await registration.showNotification("关注的人有新文案", {
    body: notice.message, tag: `follow-post:${notice.id}`, data: { url: noticeLink(notice.id) },
  });
}
